import React, { useState, useEffect, useCallback } from "react";
import { useTable } from "@/lib/store";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHead, Chip, Empty, Modal, Field, Area, Kicker } from "@/components/kit";
import { ROMAN, inr, fmtDate } from "@/lib/util";
import { DateField } from "@/components/DateField";

export default function Fines() {
  const { fines, meetings, activeMembers, memberById, yearId, refresh, me, canWrite, settings } = useTable();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ memberId: "", meetingLabel: "", reason: "", amount: 0, date: "", notes: "", paid: false });
  const schedule = settings?.fineSchedule || [];

  const isAdmin = me.isAdmin && canWrite;
  const [late, setLate] = useState({ items: [], tiers: null });
  const [busyKey, setBusyKey] = useState(null);

  const loadLate = useCallback(async () => {
    if (!yearId) return;
    try {
      const { data } = await api.get(`/fines/late-review?yearId=${yearId}`);
      setLate({ items: data?.items || [], tiers: data?.tiers || null });
    } catch { /* ignore */ }
  }, [yearId]);
  useEffect(() => { loadLate(); }, [loadLate]);

  const act = async (item, kind) => {
    setBusyKey(item.key);
    try {
      if (kind === "charge") {
        await api.post("/fines/late-review/charge", {
          yearId, key: item.key, memberId: item.memberId, reason: item.reason,
          meetingLabel: item.meetingLabel, amount: item.suggested,
          notes: `Late fee · ${item.daysOverdue} days overdue`,
        });
        toast.success("Late fee charged.");
      } else {
        await api.post("/fines/late-review/dismiss", { yearId, key: item.key });
        toast.success("Waived.");
      }
      await Promise.all([refresh(), loadLate()]);
    } catch (e) { toast.error(errMsg(e)); }
    setBusyKey(null);
  };

  const total = fines.reduce((a, f) => a + Number(f.amount || 0), 0);
  const paid = fines.filter((f) => f.paid).reduce((a, f) => a + Number(f.amount || 0), 0);

  const save = async () => {
    try {
      const payload = { ...form, amount: Number(form.amount || 0), yearId };
      if (editId) await api.put(`/fines/${editId}`, payload);
      else await api.post("/fines", payload);
      setOpen(false); setEditId(null);
      setForm({ memberId: "", meetingLabel: "", reason: "", amount: 0, date: "", notes: "", paid: false });
      await refresh();
      toast.success("Recorded.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="fines-page">
      <PageHead
        kicker="The house rules"
        title="Fines"
        sub="Small sums, kept visible. Discipline is cheaper than drift. Amounts follow the forum policy."
        right={isAdmin && <button className="btn-ink" data-testid="add-fine" onClick={() => { setEditId(null); setOpen(true); }}>Log a fine</button>}
      />

      <div className="grid grid-cols-3 gap-3 mb-6">
        {[["Total", total], ["Paid", paid], ["Outstanding", total - paid]].map(([label, val]) => (
          <Card key={label} className="p-4" data-testid={`fines-${label.toLowerCase()}`}>
            <Kicker className="!text-[9px]">{label}</Kicker>
            <div className="serif text-2xl mt-2">{inr(val)}</div>
          </Card>
        ))}
      </div>

      {late.items.length > 0 && (
        <Card className="p-5 mb-6" data-testid="late-review">
          <Kicker className="!text-[9px]">Flagged — awaiting your call</Kicker>
          <p className="text-xs mt-2 mb-4" style={{ color: "var(--ink-mute)" }}>
            Absences from a closed chapter and overdue items are flagged here. Nothing is charged
            automatically — the policy amount is suggested; charge it or waive it.
            {late.tiers && ` (${inr(late.tiers.upTo30Days)} up to 30 days, ${inr(late.tiers.beyond30Days)} beyond.)`}
          </p>
          <div className="space-y-3">
            {late.items.map((it) => (
              <div key={it.key} className="flex flex-wrap items-start gap-x-4 gap-y-2 pt-3 border-t" style={{ borderColor: "var(--hair)" }} data-testid={`late-item-${it.key}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm">{memberById(it.memberId)?.name || "Member"}</div>
                  <p className="serif text-base mt-0.5">{it.reason}</p>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <Chip>{it.kind}</Chip>
                    {it.meetingLabel && <Chip>{it.meetingLabel}</Chip>}
                    <Chip color="var(--rust)">{it.daysOverdue} {it.daysOverdue === 1 ? "day" : "days"} overdue</Chip>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <span className="serif text-xl" data-testid={`late-suggested-${it.key}`}>{inr(it.suggested)}</span>
                  {isAdmin && (
                    <>
                      <button className="btn-ink !min-h-0 !px-3 !py-2" disabled={busyKey === it.key}
                        data-testid={`late-charge-${it.key}`} onClick={() => act(it, "charge")}>Charge</button>
                      <button className="btn-quiet !min-h-0 !px-3 !py-2" disabled={busyKey === it.key}
                        data-testid={`late-dismiss-${it.key}`} onClick={() => act(it, "dismiss")}>Waive</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {fines.length === 0 ? (
        <Empty line="No fines. The room has been on time." testId="fines-empty" />
      ) : (
        <div className="space-y-3">
          {fines.map((f) => {
            const m = memberById(f.memberId);
            return (
              <Card key={f.id} className="p-4" data-testid={`fine-${f.id}`}>
                <div className="flex justify-between gap-3 items-start">
                  <div className="min-w-0">
                    <div className="text-sm">{m?.name || "Member"}</div>
                    <p className="serif text-lg mt-0.5">{f.reason}</p>
                    <div className="flex gap-2 mt-2.5 flex-wrap">
                      {f.meetingLabel && <Chip>{f.meetingLabel}</Chip>}
                      <Chip>{fmtDate(f.date)}</Chip>
                      <Chip color={f.paid ? "var(--green)" : "var(--rust)"}>{f.paid ? "Paid" : "Outstanding"}</Chip>
                    </div>
                    {f.notes && <p className="text-xs mt-2" style={{ color: "var(--ink-mute)" }}>{f.notes}</p>}
                  </div>
                  <div className="serif text-2xl shrink-0">{inr(f.amount)}</div>
                </div>
                {isAdmin && (
                  <div className="flex gap-2 mt-4 flex-wrap">
                    <button
                      className="btn-quiet"
                      data-testid={`fine-toggle-${f.id}`}
                      onClick={async () => { await api.put(`/fines/${f.id}`, { paid: !f.paid }); await refresh(); }}
                    >
                      {f.paid ? "Mark unpaid" : "Mark paid"}
                    </button>
                    <button
                      className="btn-quiet"
                      data-testid={`fine-edit-${f.id}`}
                      onClick={() => { setEditId(f.id); setForm({ ...f }); setOpen(true); }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn-quiet"
                      data-testid={`fine-delete-${f.id}`}
                      onClick={async () => { await api.delete(`/fines/${f.id}`); await refresh(); }}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit fine" : "Log a fine"} testId="fine-modal">
        <label className="block">
          <span className="kicker block mb-1.5">Member</span>
          <select className="field" data-testid="fine-member" value={form.memberId} onChange={(e) => setForm({ ...form, memberId: e.target.value })}>
            <option value="">Select</option>
            {activeMembers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="kicker block mb-1.5">Chapter</span>
          <select className="field" data-testid="fine-chapter" value={form.meetingLabel} onChange={(e) => setForm({ ...form, meetingLabel: e.target.value })}>
            <option value="">—</option>
            {meetings.map((m) => <option key={m.id} value={`Chapter ${ROMAN[m.num - 1]}`}>Chapter {ROMAN[m.num - 1]}</option>)}
          </select>
        </label>
        {schedule.length > 0 && (
          <div data-testid="fine-schedule">
            <span className="kicker block mb-2">Policy schedule — tap to apply</span>
            <div className="flex gap-2 flex-wrap">
              {schedule.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  className="chip"
                  data-testid={`fine-preset-${s.key}`}
                  onClick={() => setForm({ ...form, reason: s.reason, amount: s.amount })}
                >
                  {s.reason} · {inr(s.amount)}
                </button>
              ))}
            </div>
          </div>
        )}
        <Field label="Reason" value={form.reason} data-testid="fine-reason" onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        <Field label="Amount (₹)" type="number" value={form.amount} data-testid="fine-amount" onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        <DateField label="Date" value={form.date} testId="fine-date" onChange={(v) => setForm({ ...form, date: v })} />
        <Area label="Notes" value={form.notes} data-testid="fine-notes" onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        <button className="btn-ink w-full" data-testid="fine-save" onClick={save}>Save</button>
      </Modal>
    </div>
  );
}
